import type { ReactNode } from "react";
import type { AppTranslateFn } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import { PALETTE_ITEMS } from "../../lib/palette-metadata";
import type { BuilderNode, PaletteFieldType } from "../../types";
import { BuilderDragPreview } from "./builder-drag-preview";
import { PALETTE_PREFIX } from "./form-builder-palette";
import { translatePaletteFieldTypeLabel } from "./form-builder-palette-label";

/** Renders the floating card in the drag overlay while dragging palette items or canvas nodes. */
export function FormBuilderDragOverlayContent({
	activeDragId,
	nodes,
	t,
	paths,
}: {
	activeDragId: string;
	nodes: Record<string, BuilderNode>;
	t: AppTranslateFn;
	paths: AdminFormI18nPaths;
}): ReactNode {
	if (activeDragId.startsWith(PALETTE_PREFIX)) {
		const raw = activeDragId.slice(PALETTE_PREFIX.length) as PaletteFieldType;
		const meta = PALETTE_ITEMS.find((p) => p.type === raw);
		const label = meta
			? translatePaletteFieldTypeLabel(t, paths, meta.type)
			: raw;
		return <BuilderDragPreview variant="palette" label={String(label)} />;
	}

	const node = nodes[activeDragId];
	if (!node) return null;

	if (node.kind === "section") {
		return (
			<BuilderDragPreview
				variant="section"
				title={node.section.label}
				subtitle={String(t(paths.formEditor.builder.sectionHeader))}
			/>
		);
	}

	const typeLabel = translatePaletteFieldTypeLabel(
		t,
		paths,
		node.field.fieldType,
	);
	return (
		<BuilderDragPreview
			variant="field"
			label={node.field.label}
			typeLabel={String(typeLabel)}
			required={node.field.required}
		/>
	);
}
